import type { PlatformPaymentSettings } from '@inflip/contracts/admin';
import { BUSINESS, SCHEDULES, feeRateLabel } from '@inflip/shared';
import { useEffect, useState } from 'react';

import { Field, SavedAt, usePlatformSettings } from '../components/settings-form';
import {
  BankSelect,
  Button,
  ErrorBanner,
  LoadingRow,
  PageHeader,
  Section,
  inputCls,
} from '../components/ui';
import { api } from '../lib/api-client';

/** 결제 설정 — 무통장입금 기한 / 입금 계좌 / 구매확정 기한 + 거래 단계별 기한 한눈에 보기 */
export function PaymentSettingsPage() {
  const { session, settings, error, run } = usePlatformSettings();
  const [form, setForm] = useState<PlatformPaymentSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (settings && !form) {
      setForm({
        depositDueHours: settings.depositDueHours,
        addonDepositDueHours: settings.addonDepositDueHours,
        licenseIssueDelayDays: settings.licenseIssueDelayDays,
        purchaseConfirmDays: settings.purchaseConfirmDays,
        proposalExpiryDays: settings.proposalExpiryDays,
        platformFeeRateBp: settings.platformFeeRateBp,
        bankAccount: settings.bankAccount,
      });
    }
  }, [settings, form]);

  if (!session) return null;

  const save = async () => {
    if (!form || saving) return;
    setSaving(true);
    setSavedAt(null);
    const ok = await run(() => api.updatePaymentSettings(session.accessToken, form));
    if (ok) setSavedAt(new Date());
    setSaving(false);
  };

  const setBank = (patch: Partial<PlatformPaymentSettings['bankAccount']>) =>
    form && setForm({ ...form, bankAccount: { ...form.bankAccount, ...patch } });

  return (
    <main className="mx-auto w-full max-w-xl px-6 py-8">
      <PageHeader
        title="결제 설정"
        description="무통장입금·조건 협의·구매확정 정책을 관리해요 — 저장 즉시 새 주문·새 제안·새 발급부터 반영돼요"
        actions={
          <>
            <SavedAt at={savedAt} />
            <Button disabled={saving || !form} onClick={() => void save()}>
              {saving ? '저장 중…' : '저장'}
            </Button>
          </>
        }
      />
      {error && <ErrorBanner message={error} />}
      {!form && !error && <LoadingRow />}

      {form && (
        <Section title="무통장 입금">
          <div className="space-y-3">
            <Field
              label="입금 기한 (시간)"
              hint="주문 생성 시점부터 이 시간이 지나면 자동 만료돼요"
            >
              <input
                type="number"
                min={1}
                max={720}
                className={`w-28 ${inputCls}`}
                value={form.depositDueHours}
                onChange={(e) => setForm({ ...form, depositDueHours: Number(e.target.value) })}
              />
            </Field>
            <Field
              label="추가금 입금 기한 (시간)"
              hint="판매자가 입금 확인 뒤 추가금을 요청한 시점부터 이 시간이 지나면 그 요청만 만료돼요. 주문은 그대로예요"
            >
              <input
                type="number"
                min={1}
                max={720}
                className={`w-28 ${inputCls}`}
                value={form.addonDepositDueHours}
                onChange={(e) => setForm({ ...form, addonDepositDueHours: Number(e.target.value) })}
              />
            </Field>
            <Field label="입금 계좌 (기본)" hint="구매자 입금 안내에 표시되는 회사 계좌예요">
              <div className="flex flex-wrap gap-2">
                <BankSelect
                  className="w-40"
                  value={form.bankAccount.bankName}
                  onChange={(bankName) => setBank({ bankName })}
                />
                <input
                  className={`min-w-0 flex-1 ${inputCls}`}
                  placeholder="계좌번호"
                  value={form.bankAccount.accountNumber}
                  onChange={(e) => setBank({ accountNumber: e.target.value })}
                />
                <input
                  className={`w-32 ${inputCls}`}
                  placeholder="예금주"
                  value={form.bankAccount.holderName}
                  onChange={(e) => setBank({ holderName: e.target.value })}
                />
              </div>
            </Field>
          </div>
        </Section>
      )}

      {form && (
        <Section title="플랫폼 수수료">
          <Field
            label="수수료 (%)"
            hint="판매 대금과 로열티에서 빼는 비율이에요. 주문이 만들어질 때 주문에 고정되므로, 바꾸면 그 뒤에 만들어지는 주문부터 적용돼요. 진행 중인 거래와 이미 맺은 로열티 계약은 기존 수수료 그대로예요."
          >
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={50}
                step={0.1}
                className={`w-28 ${inputCls}`}
                value={form.platformFeeRateBp / 100}
                onChange={(e) =>
                  setForm({ ...form, platformFeeRateBp: Math.round(Number(e.target.value) * 100) })
                }
              />
              <span className="text-sm text-muted-foreground">
                예: 100,000원 판매 → 판매자 지급{' '}
                {(100_000 - Math.floor((100_000 * form.platformFeeRateBp) / 10_000)).toLocaleString(
                  'ko-KR',
                )}
                원
              </span>
            </div>
          </Field>
        </Section>
      )}

      {form && (
        <Section title="조건 협의">
          <Field
            label="응답 기한 (일)"
            hint={`제안·수정 제안·제안 수정을 보낸 뒤 이 기간 안에 상대가 응답하지 않으면 협의가 만료되고 양쪽에 알려요(${SCHEDULES.EXPIRE_PROPOSALS.label} 확인). 제안할 때 기한이 정해지므로, 바꾸면 그 뒤 제안분부터 적용돼요.`}
          >
            <input
              type="number"
              min={1}
              max={90}
              className={`w-28 ${inputCls}`}
              value={form.proposalExpiryDays}
              onChange={(e) => setForm({ ...form, proposalExpiryDays: Number(e.target.value) })}
            />
          </Field>
        </Section>
      )}

      {form && (
        <Section title="라이선스 발급">
          <Field
            label="발급 지연 기준 (일)"
            hint={`입금 확인 뒤 이 기간이 지나도록 발급되지 않은 주문을 발급 지연으로 봐요. 매일 ${SCHEDULES.LICENSE_ISSUE_DELAY_REMINDERS.label}에 판매자 채팅방으로 판매자에게만 보이는 안내를 보내고, 대시보드·주문 관리에 표시해요.`}
          >
            <input
              type="number"
              min={1}
              max={90}
              className={`w-28 ${inputCls}`}
              value={form.licenseIssueDelayDays}
              onChange={(e) => setForm({ ...form, licenseIssueDelayDays: Number(e.target.value) })}
            />
          </Field>
        </Section>
      )}

      {form && (
        <Section title="구매확정">
          <Field
            label="구매확정 기한 (일)"
            hint="사용 시작일(판매자가 발급 예정 내용에 정하고 구매자가 확인한 날, 기본은 발급일)부터 이 기간 안에 구매자가 확정하지 않으면 자동 확정되고 정산이 시작돼요. 기간 안에 분쟁이 접수되면 멈춰요. 바꾸면 그 뒤 발급분부터 적용돼요."
          >
            <input
              type="number"
              min={1}
              max={90}
              className={`w-28 ${inputCls}`}
              value={form.purchaseConfirmDays}
              onChange={(e) => setForm({ ...form, purchaseConfirmDays: Number(e.target.value) })}
            />
          </Field>
        </Section>
      )}

      {form && (
        <TradeTimeline
          depositDueHours={form.depositDueHours}
          addonDepositDueHours={form.addonDepositDueHours}
          licenseIssueDelayDays={form.licenseIssueDelayDays}
          purchaseConfirmDays={form.purchaseConfirmDays}
          proposalExpiryDays={form.proposalExpiryDays}
          platformFeeRateBp={form.platformFeeRateBp}
        />
      )}
    </main>
  );
}

/**
 * 거래 단계별 기한 — 위에서 바꾸는 값(설정)과 코드에 고정된 값(고정)을 순서대로 한눈에.
 * 고정 값·스케줄은 @inflip/shared 의 BUSINESS / SCHEDULES 를 그대로 읽는다(서버와 같은 값).
 */
function TradeTimeline({
  depositDueHours,
  addonDepositDueHours,
  licenseIssueDelayDays,
  purchaseConfirmDays,
  proposalExpiryDays,
  platformFeeRateBp,
}: {
  depositDueHours: number;
  addonDepositDueHours: number;
  licenseIssueDelayDays: number;
  purchaseConfirmDays: number;
  proposalExpiryDays: number;
  platformFeeRateBp: number;
}) {
  const rows: { step: string; value: string; detail: string; editable: boolean }[] = [
    {
      step: '협의 응답 기한',
      value: `제안 후 ${proposalExpiryDays}일`,
      detail: `상대가 응답하지 않으면 협의 만료 · ${SCHEDULES.EXPIRE_PROPOSALS.label} 확인`,
      editable: true,
    },
    {
      step: '입금 기한',
      value: `주문 후 ${depositDueHours}시간`,
      detail: `지나면 주문 자동 취소 · ${SCHEDULES.EXPIRE_PAYMENTS.label} 확인`,
      editable: true,
    },
    {
      step: '추가금 입금 기한',
      value: `추가금 요청 후 ${addonDepositDueHours}시간`,
      detail: `지나면 추가금 요청만 만료, 주문은 그대로 · ${SCHEDULES.EXPIRE_PAYMENTS.label} 확인`,
      editable: true,
    },
    {
      step: '발급 지연',
      value: `입금 확인 후 ${licenseIssueDelayDays}일`,
      detail: `지나도록 발급 전이면 판매자 채팅방에 판매자에게만 보이는 안내 · ${SCHEDULES.LICENSE_ISSUE_DELAY_REMINDERS.label} 매일 · 대시보드·주문 관리에 표시`,
      editable: true,
    },
    {
      step: '사용 시작일',
      value: `라이선스 발급일부터 최대 ${BUSINESS.LICENSE_START_MAX_DAYS_AHEAD}일 뒤`,
      detail:
        '판매자가 발급 예정 내용에서 고르는 사용 시작일의 한도 · 사용 기간·로열티 보고 기간·구매확정 기한의 기준',
      editable: false,
    },
    {
      step: '구매확정 기한',
      value: `사용 시작일부터 ${purchaseConfirmDays}일`,
      detail: `구매자가 확정하지 않아도 지나면 자동 확정되고 정산 시작 · ${SCHEDULES.CONFIRM_PURCHASES_AND_SETTLE.label} 확인 · 그 전에 분쟁이 접수되면 멈춤`,
      editable: true,
    },
    {
      step: '자동 확정 예정 알림',
      value: `기한 ${BUSINESS.PURCHASE_CONFIRM_REMINDER_DAYS}일 전`,
      detail: `구매자에게 1회 안내 · ${SCHEDULES.PURCHASE_CONFIRM_REMINDERS.label}`,
      editable: false,
    },
    {
      step: '정산 생성',
      value: '구매확정 즉시',
      detail: `자동 확정분과 환불이 끝난 뒤 미뤄진 건은 ${SCHEDULES.CONFIRM_PURCHASES_AND_SETTLE.label}`,
      editable: false,
    },
    {
      step: '정산 지급',
      value: '어드민 처리',
      detail: `기한 없음 · 분쟁 중·보류 중·환불 진행 중이면 지급 불가`,
      editable: false,
    },
    {
      step: '정산 회수 기한',
      value: `회수 요청 후 ${BUSINESS.PAYOUT_CLAWBACK_DUE_DAYS}일`,
      detail: `정산 지급 뒤 환불은 판매자에게서 먼저 돌려받고 송금 · ${BUSINESS.PAYOUT_CLAWBACK_REMINDER_DAYS}일 전 알림, 지나면 판매자 접속 정지 · ${SCHEDULES.PAYOUT_CLAWBACK_DUE.label} 확인`,
      editable: false,
    },
    {
      step: '플랫폼 수수료',
      value: `${feeRateLabel(platformFeeRateBp)} (주문 시점 고정)`,
      detail: '판매 대금·로열티에서 빼고 지급 · 바꾸면 그 뒤에 만들어지는 주문부터 적용',
      editable: true,
    },
    {
      step: '로열티 매출 보고',
      value: '계약마다 매달 N일',
      detail: `${BUSINESS.ROYALTY_REPORT_REMINDER_DAYS}일 전부터 리마인더, 지나면 연체 알림 · ${SCHEDULES.ROYALTY_REPORT_REMINDERS.label}`,
      editable: false,
    },
    {
      step: '라이선스 만료',
      value: '계약 기간 종료일',
      detail: `만료 처리 · ${SCHEDULES.EXPIRE_LICENSES.label}`,
      editable: false,
    },
  ];
  return (
    <Section title="거래 단계별 기한">
      <p className="text-xs text-muted-foreground">
        ‘설정’은 이 페이지에서 바꿀 수 있고, ‘고정’은 코드에 정해진 값이에요(변경은 개발 배포 필요).
        시각은 따로 적지 않으면 한국 시간이에요.
      </p>
      <ol className="mt-3 divide-y divide-border/60">
        {rows.map((r, i) => (
          <li
            key={r.step}
            className="grid grid-cols-[1.5rem_1fr_auto] items-baseline gap-x-2 py-2.5"
          >
            <span className="text-xs font-semibold tabular-nums text-muted-foreground">
              {i + 1}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold">
                {r.step} <span className="ml-1 font-bold">{r.value}</span>
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{r.detail}</p>
            </div>
            <span
              className={`text-xs font-bold ${r.editable ? 'text-foreground' : 'text-muted-foreground'}`}
            >
              {r.editable ? '설정' : '고정'}
            </span>
          </li>
        ))}
      </ol>
    </Section>
  );
}
